import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { MdSearch, MdOutlinePersonSearch } from "react-icons/md";
import {
  StyledSelect,
  StyledSelectContainer,
  StyledSelectGroup,
} from "../../../StyledCss/styledInputs";
import { getMyReferrals, getAllReferrals } from "../../../api/services/hroneApi";
import { normalizeReferral } from "../../../util/hiringUtils";
import "../JobListPage/index.css";
import "./index.css";

// referral.appliedDate is normalized to "DD-MM-YYYY" (see normalizeApplication).
const parseDDMMYYYY = (str) => {
  if (!str) return null;
  const [d, m, y] = str.split("-").map(Number);
  if (!d || !m || !y) return null;
  return new Date(y, m - 1, d);
};

const STATUS_LABELS = {
  APPLIED: "Applied",
  SHORTLISTED: "Shortlisted",
  HIRED: "Hired",
  REJECTED: "Rejected",
};

const STATUS_PILL_BG = {
  SHORTLISTED: "#fdf746",
  HIRED:       "#88E788",
  REJECTED:    "#FFA500",
};

const STATUS_PILL_TEXT = {
  SHORTLISTED: "#856404",
  HIRED:       "#155724",
  REJECTED:    "#7a3200",
};

// APPLIED uses the tenant's own primary color instead of a fixed blue, so
// the pill matches the org's branding — other statuses stay fixed/semantic.
const getStatusPillBg = (status, primaryColor) =>
  status === "APPLIED" ? `${primaryColor}1a` : (STATUS_PILL_BG[status] || "#eff6ff");

const getStatusPillText = (status, primaryColor) =>
  status === "APPLIED" ? primaryColor : (STATUS_PILL_TEXT[status] || "#2563eb");

const MyReferralsList = ({ scope = "mine" }) => {
  const navigate = useNavigate();
  const orgDetails = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#e86020";
  const isAll = scope === "all";
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [jobFilter, setJobFilter] = useState("ALL");
  const [dateFilter, setDateFilter] = useState("ALL");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await (isAll ? getAllReferrals() : getMyReferrals());
        const data = await res.json();
        if (!cancelled) {
          if (data.status) {
            setReferrals((data.data?.referrals || data.data || []).map(normalizeReferral));
          } else {
            toast.error(data.message || "Failed to load referrals");
          }
        }
      } catch {
        if (!cancelled) toast.error("Failed to load referrals");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isAll]);

  const jobOptions = useMemo(
    () => [...new Set(referrals.map((r) => r.jobTitle).filter(Boolean))].sort(),
    [referrals]
  );

  const filteredReferrals = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const now = new Date();
    return referrals.filter((r) => {
      if (statusFilter !== "ALL" && r.status !== statusFilter) return false;
      if (jobFilter !== "ALL" && r.jobTitle !== jobFilter) return false;
      if (dateFilter !== "ALL") {
        const applied = parseDDMMYYYY(r.appliedDate);
        if (!applied) return false;
        const daysAgo = Math.floor((now - applied) / (1000 * 60 * 60 * 24));
        if (dateFilter === "TODAY" && daysAgo !== 0) return false;
        if (dateFilter === "LAST_7" && daysAgo > 7) return false;
        if (dateFilter === "LAST_30" && daysAgo > 30) return false;
        if (dateFilter === "THIS_MONTH" && (applied.getMonth() !== now.getMonth() || applied.getFullYear() !== now.getFullYear())) return false;
      }
      if (q) {
        const name = r.name || `${r.firstName} ${r.lastName}`;
        const haystack = `${name} ${r.email} ${r.jobTitle}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [referrals, searchQuery, statusFilter, jobFilter, dateFilter]);

  const goToReferral = (referral) => {
    navigate(`/hiring/my-referrals/${referral.id}`, { state: { referral, scope } });
  };

  const filtersRow = (
    <div className="hiring-filters-bar mrl-filters-bar">
      <div className="mrl-filter-group">
        <div className="hiring-search-box mrl-filter-item">
          <MdSearch className="hiring-search-icon" />
          <input
            type="text"
            className="hiring-search-input"
            placeholder={isAll ? "Search by candidate, email or job…" : "Search by candidate or job title…"}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <StyledSelectGroup className="mrl-filter-item">
          <StyledSelectContainer>
            <StyledSelect width="100%" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="ALL">All Status</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </StyledSelect>
          </StyledSelectContainer>
        </StyledSelectGroup>

        <StyledSelectGroup className="mrl-filter-item">
          <StyledSelectContainer>
            <StyledSelect width="100%" value={jobFilter} onChange={(e) => setJobFilter(e.target.value)}>
              <option value="ALL">All Jobs</option>
              {jobOptions.map((title) => (
                <option key={title} value={title}>{title}</option>
              ))}
            </StyledSelect>
          </StyledSelectContainer>
        </StyledSelectGroup>

        <StyledSelectGroup className="mrl-filter-item">
          <StyledSelectContainer>
            <StyledSelect width="100%" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
              <option value="ALL">Any Posted Date</option>
              <option value="TODAY">Today</option>
              <option value="LAST_7">Last 7 Days</option>
              <option value="LAST_30">Last 30 Days</option>
              <option value="THIS_MONTH">This Month</option>
            </StyledSelect>
          </StyledSelectContainer>
        </StyledSelectGroup>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="mrl-wrapper">
        <div className="mrl-card">
          <div className="mrl-empty">
            <p>Loading referrals…</p>
          </div>
        </div>
      </div>
    );
  }

  if (referrals.length === 0) {
    return (
      <div className="mrl-wrapper">
        <div className="mrl-card">
          <div className="mrl-empty">
            <div className="mrl-empty-illustration"><MdOutlinePersonSearch /></div>
            <p className="mrl-empty-title">
              {isAll ? "No candidates have been referred yet" : "You haven't referred any candidates yet"}
            </p>
            <p className="mrl-empty-message">
              {isAll
                ? "Referrals made by anyone in the organization will show up here."
                : "Refer a candidate from any open job to see them listed here."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mrl-wrapper">
      {filtersRow}

      <div className="mrl-card">
        {filteredReferrals.length === 0 ? (
          <div className="mrl-empty">
            <div className="mrl-empty-illustration"><MdOutlinePersonSearch /></div>
            <p className="mrl-empty-title">No referrals match your filters</p>
            <p className="mrl-empty-message">Try adjusting or resetting the filters above.</p>
          </div>
        ) : (
          <div className="mrl-scroll">
            <div className={`mrl-header ${isAll ? "mrl-header--all" : ""}`}>
              <span className="mrl-col-name">CANDIDATE NAME</span>
              <span className="mrl-col-job">JOB TITLE</span>
              <span className="mrl-col-email">EMAIL</span>
              <span className="mrl-col-mobile">MOBILE NUMBER</span>
              <span className="mrl-col-status">STATUS</span>
              <span className="mrl-col-date">APPLIED DATE</span>
              {isAll && <span className="mrl-col-referrer">REFERRED BY</span>}
            </div>

            {filteredReferrals.map((referral) => (
              <div
                key={referral.id}
                className={`mrl-row ${isAll ? "mrl-row--all" : ""}`}
                onClick={() => goToReferral(referral)}
              >
                <div className="mrl-cell mrl-col-name">{referral.name || `${referral.firstName} ${referral.lastName}`}</div>
                <div className="mrl-cell mrl-col-job">{referral.jobTitle || "—"}</div>
                <div className="mrl-cell mrl-col-email">{referral.email}</div>
                <div className="mrl-cell mrl-col-mobile">{referral.mobile}</div>
                <div className="mrl-cell mrl-col-status">
                  <span
                    className="mrl-status-pill"
                    style={{
                      background: getStatusPillBg(referral.status, primaryColor),
                      color: getStatusPillText(referral.status, primaryColor),
                    }}
                  >
                    {STATUS_LABELS[referral.status] || referral.status}
                  </span>
                </div>
                <div className="mrl-cell mrl-col-date">{referral.appliedDate || "—"}</div>
                {isAll && <div className="mrl-cell mrl-col-referrer">{referral.referredByName || "—"}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyReferralsList;
